import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { notEmpty } from '@crehana/utils';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';

import CATALOG_COURSES_QUERY from '../graphql/CatalogCourses.graphql';
import type {
  CatalogCourses,
  CatalogCoursesVariables,
} from '../graphql/types/CatalogCourses';
import {
  getCategorySelected,
  getLevelsSelected,
  getSelectedDurations,
  getSelectedLanguages,
  getSoftwaresSelected,
} from '../components/FiltersDesktop/utils';
import { useAppContext } from '../context/AppContext';
import {
  checkIfisInShoppingCart,
  COURSES_PAGE_SIZE,
  formatCourseV2,
  getCursor,
} from '../utils';

function useCatalogCoursesQuery() {
  const {
    filters,
    orderBy: { itemSelected },
    currentPage,
    filterActions,
  } = useAppContext();
  const { categorySelected, subCategoriesSelected } =
    getCategorySelected(filters);
  const levelsSelected = getLevelsSelected(filters);
  const selectedLanguages = getSelectedLanguages(filters);
  const softwaresSelected = getSoftwaresSelected(filters);
  const selectedDurations = getSelectedDurations(filters);

  const categories = categorySelected?.slug;
  const subCategories = categorySelected
    ? subCategoriesSelected.map(sc => sc.slug).join(',')
    : null;
  const software = softwaresSelected.map(soft => soft.slug).join(',');

  const { data, loading } = useQuery<CatalogCourses, CatalogCoursesVariables>(
    CATALOG_COURSES_QUERY,
    {
      variables: {
        pageSize: COURSES_PAGE_SIZE,
        newCategories: categories,
        newSubCategories: subCategories,
        newSoftwares: software,
        language: selectedLanguages.map(el => el.slug).join(','),
        newOrderBy: itemSelected.value,
        level: levelsSelected.map(level => level.slug).join(','),
        duration: selectedDurations.join(','),
        cursor: getCursor(currentPage, COURSES_PAGE_SIZE),
        includeFilters: Boolean(categories || subCategories || software),
      },
    },
  );

  const {
    shoppingCartState: { courseIdsInCart },
  } = useProductsContainer();

  const {
    edges,
    filteredSoftwares,
    filteredLevels,
    totalCount = 0,
  } = data?.courses || {};

  const courses = useMemo(
    () =>
      edges
        ?.map(e => e?.node)
        .filter(notEmpty)
        .map(course => {
          // @ts-expect-error
          const formatted = formatCourseV2(course);

          return checkIfisInShoppingCart(courseIdsInCart)(formatted);
        }) || [],
    [edges, courseIdsInCart],
  );

  const totalPages = Math.ceil(totalCount! / COURSES_PAGE_SIZE);

  useEffect(() => {
    filterActions.disableFiltersSoftwareLevels({
      softwares: filteredSoftwares?.filter(notEmpty),
      levels: filteredLevels?.filter(notEmpty),
    });
  }, [filteredSoftwares, filteredLevels]);

  return { courses, totalCount, totalPages, loading };
}

export default useCatalogCoursesQuery;
